import { Controller } from '@hotwired/stimulus';
import offlineProgressSync from '../src/react/helpers/OfflineProgressSync';
import { seemsOffline } from '../utils/connectivity';
import { isPwaEnabled } from '../utils/offline_gating';

export const FROZEN_TIMER_KEY = 'assessmentOfflineFrozenTimer';

export default class extends Controller {
  static targets = ['form'];
  static outlets = ['offline-navigation'];

  static values = {
    rosteringId: Number,
    itemIndex: Number,
    mode: String,
    nextQuestionUrl: String,
    correctAnswerIndex: Number,
    answerLetter: String,
    answerExplanation: String,
    autoAddTodos: Boolean,
  };

  connect() {
    this.startTime = Date.now();
    this.pwaEnabled = isPwaEnabled();
    this.sawOffline = !navigator.onLine;
    this.boundUpdateOfflineBanner = this.updateOfflineBanner.bind(this);
    this.boundHandleBlockedAction = this.handleBlockedAction.bind(this);
    this.updateOfflineBanner();
    window.addEventListener('online', this.boundUpdateOfflineBanner);
    window.addEventListener('offline', this.boundUpdateOfflineBanner);
    document.addEventListener('click', this.boundHandleBlockedAction, true);
  }

  disconnect() {
    if (this.boundUpdateOfflineBanner) {
      window.removeEventListener('online', this.boundUpdateOfflineBanner);
      window.removeEventListener('offline', this.boundUpdateOfflineBanner);
    }
    if (this.boundHandleBlockedAction) {
      document.removeEventListener('click', this.boundHandleBlockedAction, true);
    }
  }

  updateOfflineBanner() {
    const isOnline = navigator.onLine;
    const banner = document.getElementById('offline-indicator');
    if (banner) {
      banner.classList.toggle('hidden', isOnline);
    }
    const syncStatus = document.getElementById('sync-status');
    if (syncStatus) {
      syncStatus.classList.toggle('hidden', isOnline);
    }
    this.holdExamClockWhileWaiting(isOnline);
  }

  holdExamClockWhileWaiting(isOnline) {
    if (!this.pwaEnabled) return;

    if (!isOnline) {
      this.sawOffline = true;
      if (this.stillAnswering()) return;
      this.freezeExamTimer();
      return;
    }

    if (!this.sawOffline || window.location.pathname.endsWith('/edit')) {
      this.sawOffline = false;
      return;
    }

    this.sawOffline = false;
    offlineProgressSync.creditFrozenExamTime?.(this.rosteringIdValue);
  }

  stillAnswering() {
    return window.location.pathname.endsWith('/edit') && !this.revealRendered;
  }

  handleBlockedAction(event) {
    if (!this.pwaEnabled || !seemsOffline()) return;

    const clickable = event.target.closest('a, button, .pause-wrapper, .report-issue, .support_btn, .add-todo-button, .todo-manage-button');
    if (!clickable || !this.isOfflineBlockedAction(clickable)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    if (this.hasOfflineNavigationOutlet) {
      this.offlineNavigationOutlet.showFeatureUnavailable(clickable);
    } else {
      this.showFlash('This action needs an internet connection. Please reconnect, then try again.');
    }
  }

  isOfflineBlockedAction(element) {
    if (element.closest('#pauseButton, .pause-wrapper')) return true;
    if (element.closest('.report-issue')) return true;
    if (element.closest('.support_btn')) return true;
    if (element.closest('.next-question')) return true;
    if (element.matches('.summary-btn') || element.closest('.summary-btn')) return true;
    if (element.matches('.add-todo-button, .todo-manage-button') || element.closest('.add-todo-button, .todo-manage-button')) {
      return true;
    }

    const action = element.getAttribute('data-action') || '';
    return (
      action.includes('countdown-timer#showPauseRationaleModal') ||
      action.includes('to-do-modal#openTodoModal') ||
      action.includes('saq#openTodoModal') ||
      action.includes('content-library-modal#openTodoModal') ||
      action.includes('text-decoration#openTodoModal')
    );
  }

  setResponseTime() {
    if (!this.hasFormTarget) return;
    const input = this.formTarget.querySelector('#responseTime');
    if (input) {
      input.value = Date.now() - (this.startTime || Date.now());
    }
  }

  serializeForm() {
    const payload = {};
    if (!this.hasFormTarget) return payload;

    const formData = new FormData(this.formTarget);
    formData.forEach((value, key) => {
      if (payload[key]) {
        payload[key] = Array.isArray(payload[key])
          ? [...payload[key], value]
          : [payload[key], value];
      } else {
        payload[key] = value;
      }
    });

    return payload;
  }

  showFlash(message) {
    const flash = document.getElementById('saq-player-flash-messages');
    if (flash) {
      flash.textContent = message;
      flash.classList.remove('hidden');
    }
  }

  async handleSubmit(event) {
    this.setResponseTime();

    if (!this.pwaEnabled) return;
    const offlineSync = offlineProgressSync;
    if (!offlineSync) return;

    const shouldHandleOffline = offlineSync.shouldQueue() || !navigator.onLine;
    if (!shouldHandleOffline) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    try {
      const payload = this.serializeForm();
      await offlineSync.queueAssessmentItemResponse({
        rosteringId: this.rosteringIdValue,
        itemIndex: this.itemIndexValue,
        mode: this.modeValue,
        formData: payload,
        nextQuestionUrl: this.nextQuestionUrlValue || '',
        currentPath: window.location.pathname,
      });
      window.dispatchEvent(new CustomEvent("assessment-offline:queued"));

      if (this.nextQuestionUrlValue) {
        localStorage.setItem('assessmentOfflinePendingNextUrl', this.nextQuestionUrlValue);
      }
      this.freezeExamTimer();
      this.renderOfflineShowFromPayload(payload);
      this.disableSubmitControls();
    } catch (error) {
      console.error('[AssessmentOffline] Failed to queue response', error);
      this.showFlash('Unable to save your answer offline. Please reconnect and try again.');
    }

    return false;
  }

  freezeExamTimer() {
    const timersEl = document.getElementById('timers');
    if (!timersEl) return;

    const timerController = this.application.getControllerForElementAndIdentifier(
      timersEl,
      'countdown-timer'
    );
    if (!timerController?.freezeExamClock || timerController.examClockFrozen || timerController.pauseInterval) return;

    const remainingSeconds = timerController.freezeExamClock();
    try {
      localStorage.setItem(
        FROZEN_TIMER_KEY,
        JSON.stringify({
          rosteringId: this.rosteringIdValue,
          remainingSeconds,
          savedAt: Date.now(),
          frozen: true,
        })
      );
    } catch (_error) {
    }
  }

  disableSubmitControls() {
    if (!this.hasFormTarget) return;
    this.formTarget.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
    });
  }

  renderOfflineShowFromPayload(payload = {}) {
    if (this.revealRendered) return;
    this.revealRendered = true;

    const questionView = this.element.querySelector('.question_view');
    if (!questionView) return;

    const selectedAnswerRaw = payload['assessment_item_response[answer_index]'];
    const selectedAnswerIndex = selectedAnswerRaw !== undefined ? Number(selectedAnswerRaw) : null;
    const selectedConfidence = payload['assessment_item_response[confidence]'];
    const correctAnswerIndex = Number(this.correctAnswerIndexValue);
    const choiceLabels = Array.from(this.element.querySelectorAll('li.player-form-check label.md-body-copy'));

    const choicesHtml = choiceLabels
      .map((label, index) => {
        const choiceText = label.cloneNode(true);
        choiceText.querySelectorAll('input').forEach((el) => el.remove());
        const isCorrect = Number(correctAnswerIndex) === index;
        const isSelected = Number(selectedAnswerIndex) === index;
        const choiceLetter = String.fromCharCode(65 + index);
        const confidenceChip = isSelected && selectedConfidence
          ? `<button disabled class="player-form-check-label" style="margin-left: 15px;font-family: Roboto Condensed;font-size: 10px;font-weight: 700;height: 28px;">${selectedConfidence.replace('_', ' ')}</button>`
          : '';

        return `
          <div class="item-flag-container ${isCorrect ? 'highlight correct' : ''}" style="margin-bottom: 15px;display: flex;align-items: center;">
            ${isCorrect ? '<span class="left-image">✔</span>' : ''}
            <label>
              <input type="radio" disabled ${isSelected ? 'checked' : ''} style="margin-right: 15px" class="saq-response-radio-button">
            </label>
            <div>
              <label style="font-weight: normal; font-family: 'Roboto';">${choiceLetter}. ${choiceText.innerHTML}</label>
              ${confidenceChip}
            </div>
          </div>
        `;
      })
      .join('');

    questionView.innerHTML = `
      <div class="offline-assessment-show">
        <p><strong>Offline answer saved locally.</strong></p>
        <div><strong>The correct answer is ${this.answerLetterValue || ''}</strong></div>
        <ul>${choicesHtml}</ul>
        <h4 class="answer-explanation-header">ANSWER EXPLANATION</h4>
        <div>Answer explanations are unavailable offline. Please reconnect to view them.</div>
        ${this.autoAddTodosValue && selectedAnswerIndex !== correctAnswerIndex ? '<p>Related case will be added to your To-do list when you reconnect.</p>' : ''}
      </div>
      ${this.nextQuestionUrlValue ? `
        <div style="text-align: right; margin-top: 16px;">
          <button type="button" class="aq-button-2 offline-next-button">Continue to next question</button>
        </div>
      ` : ''}
    `;

    if (this.nextQuestionUrlValue) {
      const nextButton = questionView.querySelector('.offline-next-button');
      nextButton?.addEventListener('click', () => this.handleOfflineNextClick());
    }
  }

  handleOfflineNextClick() {
    if (!this.nextQuestionUrlValue) return;

    const offline = seemsOffline();
    if (offline) {
      localStorage.setItem('assessmentOfflinePendingNextUrl', this.nextQuestionUrlValue);
      if (this.hasOfflineNavigationOutlet) {
        this.offlineNavigationOutlet.showFeatureUnavailable();
      } else {
        this.showFlash('This action needs an internet connection. Please reconnect, then try again.');
      }
      return;
    }

    window.location.assign(this.nextQuestionUrlValue);
  }
}
